import React from 'react';
import { Button } from '@/components/ui/button';

interface Props { 
  children: React.ReactNode;
}

interface State { 
  hasError: boolean; 
  isChunkError: boolean;
  error?: Error;
}

/**
 * Error boundary specifically for handling lazy-loaded chunk failures.
 * Provides a friendly retry option for network-related loading issues.
 */
export class ChunkErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk loading error
    const isChunkError = 
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Unable to preload CSS');
    
    return { hasError: true, isChunkError, error };
  }

  componentDidCatch(error: Error) {
    // Only log in development
    if (import.meta.env.DEV) {
      console.error('[ChunkErrorBoundary]', error);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, isChunkError: false, error: undefined });
    // Force re-render which will attempt to load the chunk again
    this.forceUpdate();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="flex items-center justify-center min-h-[40vh] p-6">
            <div className="max-w-sm w-full text-center space-y-4">
              <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center">
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="space-y-1">
                <h3 className="font-medium text-foreground">Connection issue</h3>
                <p className="text-sm text-muted-foreground">
                  This page couldn't load. Please check your connection and try again.
                </p>
              </div>
              <div className="flex gap-2 justify-center">
                <Button onClick={this.handleRetry} size="sm">
                  Try Again
                </Button>
                <Button onClick={this.handleReload} variant="outline" size="sm">
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        );
      }

      // For non-chunk errors, show generic error
      return (
        <div className="flex items-center justify-center min-h-[40vh] p-6">
          <div className="max-w-sm w-full text-center space-y-4">
            <h3 className="font-medium text-foreground">Something went wrong</h3>
            <Button onClick={this.handleReload} size="sm">
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
